import { createFileRoute } from "@tanstack/react-router";
import { ProvenanceTemplate, VideoTutorialBox } from "./-components";
import type { ProvenanceV2EntryBase } from "@/lib/types/provenance";
import { quillbotAnalysis } from "@/lib/textAnalyze";
import { cn } from "@/lib/utils";
import { Progress } from "@/components/ui/progress";

export const Route = createFileRoute(
  "/_app/_provenance/humanized-text-detector",
)({
  component: RouteComponent,
  staticData: {
    title: "Humanized Text Detection",
  },
  head: () => ({
    meta: [
      {
        name: "description",
        content:
          "Identify text subtly enhanced by AI with Polygraf AI’s Humanized Text Detector - preserve authentic voice by catching meta and semantic traces, deployable in local or on-premises environments.",
      },
    ],
  }),
});

function RouteComponent() {
  const { title } = Route.useMatch({
    select: (m) => m.staticData,
  });
  return (
    <div className="relative">
      <VideoTutorialBox
        title="New to Polygraf?"
        description={`Learn how to use \n Humanized Text Detector`}
        youtubeUrl="https://youtu.be/OM__T6msWXo"
      />
      <ProvenanceTemplate
        title={title!}
        subtitle="Check if text is humanized or retains AI traces."
        model="QUILLBOT_ANALYSIS"
        analyze={(text) => quillbotAnalysis(text)}
        SuccessSection={DetectedPercentages}
        nextPage={{
          title: "Copyright Detector",
          linkOptions: {
            to: "/copyright-detector",
          },
        }}
        __addHumanizeButton
      />
    </div>
  );
}

function DetectedPercentages({
  entry: { result },
}: {
  entry: ProvenanceV2EntryBase<"QUILLBOT_ANALYSIS", string>;
}) {
  const { percentage, label } =
    result.Altered > result.Original
      ? { percentage: result.Altered * 100, label: "Altered" }
      : { percentage: result.Original * 100, label: "Original" };
  return (
    <>
      {/* <div className="mt-4 mb-4 h-[2px] bg-gray-200 dark:bg-gray-800" /> */}
      <div className="mb-2 rounded-lg border-2 border-gray-200 bg-[#FAFAFA] p-4 dark:border-gray-800 dark:bg-transparent">
        <div className="mb-4">
          <p className="mb-2">
            This content is{" "}
            <span className="font-semibold">
              {percentage.toFixed(2)}% {label}
            </span>
          </p>
          <Progress
            value={result.Altered * 100}
            className="bg-[#00E396]"
            indicatorClassName="bg-[#F9A544]"
          />
        </div>
        <div className="mb-6 flex justify-between text-sm">
          {result.Altered > 0.03 ? (
            <div className="rounded-lg border-2 bg-white p-2 dark:bg-transparent">
              <span className={cn("font-medium", "text-[#F9A544]")}>
                Altered
              </span>{" "}
              {(result.Altered * 100).toFixed(1)}%
            </div>
          ) : null}
          {result.Original > 0.03 ? (
            <div className="rounded-lg border-2 bg-white p-2 dark:bg-transparent">
              <span className={cn("font-medium", "text-[#00E396]")}>
                Original
              </span>{" "}
              {(result.Original * 100).toFixed(1)}%
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
